/**
 * list_environments tool - List all Power Platform environments
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const listEnvironmentsInputSchema = z.object({});

export type ListEnvironmentsInput = z.infer<typeof listEnvironmentsInputSchema>;

export const listEnvironmentsTool = {
  name: "list_environments",
  description:
    "List all Power Platform environments accessible to the current user. Shows environment names, IDs, regions, and whether each is the default environment.",
  inputSchema: {
    type: "object" as const,
    properties: {},
  },
};

export interface EnvironmentResult {
  id: string;
  displayName: string;
  region: string;
  isDefault: boolean;
  type: string;
  state: string;
}

export async function handleListEnvironments(
  api: FlowManagementApi,
  _input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  logger.info("Executing list_environments");

  const environments = await api.listEnvironments();

  if (environments.length === 0) {
    return { text: "No environments found", structured: { count: 0, environments: [] } };
  }

  const lines = [`Environments (${environments.length}):`];
  for (const env of environments) {
    const def = env.properties.isDefault ? " (default)" : "";
    const region = env.properties.azureRegionHint ?? "unknown";
    // The id is what every other tool's `environment` parameter takes, so a
    // listing that hides it cannot be used to target what it lists.
    lines.push(`  ${env.properties.displayName}${def} - ${region} — ${env.name}`);
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: environments.length,
    environments: environments.map((env) => ({
      id: env.name,
      displayName: env.properties.displayName,
      region: env.properties.azureRegionHint ?? "unknown",
      isDefault: Boolean(env.properties.isDefault),
    })),
  };

  return { text: lines.join("\n"), structured };
}
